import React, { useState, useEffect } from 'react';
import {
    Spin, Button, Card, Tag, message, Tooltip, Input, Space, Table, Modal, Form, Select, InputNumber
} from 'antd';
import {
    AppstoreOutlined,
    StopOutlined,
    CheckOutlined,
    EditOutlined
} from '@ant-design/icons';
import 'antd/dist/antd.css';
import { useHistory } from "react-router-dom";
import Fuse from 'fuse.js';
import _ from 'lodash';
import moment from 'moment';
import * as Service from '../core/Service';
import NavAndSideFrame from '../components/NavAndSideFrame';
import { useTranslation } from 'react-i18next';
import { displayIsActive } from '../components/table/DisplayMethod';

const title = `司機儲值紀錄`;
const selectedKey = `driver_store_value_list`;
const tableIdName = `driver_record_id`;
const filterableField = [
    'driver_name',
    'email',
    'mobile',
    'call_sign'
];

const DriverStoreValueList = (props) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    const history = useHistory();
    const [dataList, setDataList] = useState([]);
    const [unChangeData, setUnChangeData] = useState([]);
    const [isChangingIsActive, setChangingIsActive] = useState(false);
    const [loading, setLoading] = useState(false);

    const [modalVisible, setModalVisible] = useState(false);

    useEffect(() => {
        getAllData();
    }, []);

    const getAllData = async () => {
        let endpoint = `/api/driver/store-value`;
        let list = [];
        setLoading(true);
        try {
            let resp = await Service.call(`GET`, endpoint);

            if (resp.status === 1) {
                list = resp.data;
            }
        } catch (error) {
            console.log(error);
        } finally {
            setDataList(list);
            setUnChangeData(list);
            setLoading(false);
        }
    }



    const createNewRecord = async (formObj) => {
        let method = `POST`;
        let endpoint = `/api/driver/store-value`;

        let bodyObj = _.clone(formObj);

        try {
            let resp = await Service.call(method, endpoint, bodyObj);
            if (resp.errorMessage) {
                message.error(resp.errorMessage);
                return;
            }
            message.success(t('common.success'));
            form.resetFields();
            setModalVisible(false);
            getAllData()
        } catch (error) {
            form.resetFields();
            console.log(error);
        }
    }

    const setTableHeader = () => {
        let columns = [
            {
                title: '操作',
                dataIndex: tableIdName,
                render: (value, record) => {
                    return (
                        <span>
                            <Tooltip title={t('common.view_details')}>
                                <Button
                                    shape="circle"
                                    style={{ marginRight: 8, color: '#000000' }}
                                    icon={<AppstoreOutlined />}
                                    onClick={() => {
                                        displayDriverDetails(record.driver_id);
                                    }}
                                />
                            </Tooltip>
                            {record.is_active === 0 && (
                                <Tooltip title="審核">
                                    <Button
                                        shape="circle"
                                        style={{ color: record.is_active ? 'red' : 'green' }}
                                        icon={record.is_active ? (<StopOutlined />) : (<CheckOutlined />)}
                                        onClick={async () => {
                                            if (isChangingIsActive) return; // avoid multiple attempts

                                            setChangingIsActive(true);
                                            let { driver_id, is_active } = record;
                                            is_active = !is_active;

                                            let result = await Service.call('put', `/api/driver/store-value`, { [tableIdName]: value, is_active });
                                            setChangingIsActive(false);

                                            if (_.isEmpty(result) || result.status < 1) {
                                                return message.error("審核失敗");
                                            }
                                            await getAllData();
                                            return message.success("審核成功");
                                        }}
                                    />
                                </Tooltip>
                            )}
                        </span>
                    )
                }
            },
            {
                title: '呼號',
                dataIndex: 'call_sign',
                render: (value) => {
                    if (_.isNil(value)) return "-";
                    const child = _.map(value.split(','), (cs) => {
                        return <Tag>{cs}</Tag>
                    });
                    return <div style={{ maxWidth: "10vw", display: "flex", flexWrap: "wrap" }}>{child}</div>
                },
                ellipsis: true,
            },
            { title: '司機編號', dataIndex: 'driver_id' },
            { title: '儲值額', dataIndex: 'store_value' },
            {
                title: '生效狀態',
                dataIndex: 'is_active',
                render: (value) => {
                    return displayIsActive(value, t);
                },
                ellipsis: true
            },
            {
                title: '創建時間',
                dataIndex: 'ctime',
                render: (value) => {
                    return displayMoment(value);
                },
                ellipsis: true,
            },
            {
                title: '更新時間',
                dataIndex: 'utime',
                render: (value) => {
                    return displayMoment(value);
                },
                ellipsis: true,
            },
        ];
        return columns;
    }

    const displayMoment = (unixTime, outputFormat = 'YYYY/MM/DD HH:mm') => {
        if (unixTime !== 0) {
            let displayTime = moment.unix(unixTime).format(outputFormat);
            return displayTime;
        }
        return '-';
    }

    const getDataBySearchKeyword = (keywords) => {
        let dataList = [];
        try {
            const options = {
                includeScore: true,
                findAllMatches: true,
                threshold: 0.15,
                keys: filterableField
            }
            const fuse = new Fuse(unChangeData, options)
            const result = fuse.search(keywords);
            console.log("RESULT", result);
            dataList = _.map(result, 'item');
            dataList = _.orderBy(dataList, [`ctime`], [`desc`]);
        } catch (error) {
            console.error('error >>> ', error);
        } finally {
            setDataList(dataList);
        }
    }

    let timeout;
    const debounce = (func, delay) => {
        clearTimeout(timeout);
        timeout = setTimeout(func, delay);
    };

    const onChange = (keyword) => {
        debounce(() => {
            getDataBySearchKeyword(keyword);
            if (_.isEmpty(keyword)) setDataList(unChangeData);
        }, 400);
    };

    const displayDriverDetails = (driver_id) => {
        let redirectedLink = `/driver_info?driver_id=${driver_id}`;
        history.push(redirectedLink);
    }

    return (
        <NavAndSideFrame {...props} title={title} selectedKey={selectedKey}>
            <Card>
                <Button
                    icon={<EditOutlined />}
                    style={{ marginRight: 10 }}
                    type="primary"
                    onClick={() => {
                        setModalVisible(true);
                    }}
                >
                    新增
                </Button>

                <Space direction="horizontal">
                    <>以文字搜尋:</>
                    <Input
                        placeholder="請輸入文字"
                        onChange={(values) => {
                            onChange(values.target.value);
                        }}
                    />
                </Space>
            </Card>
            <Card>
                <Spin spinning={loading}>
                    <Table
                        pagination={{
                            defaultPageSize: 25,
                            showSizeChanger: true,
                            pageSizeOptions: [25, 50, 100, 200, 500]
                        }}

                        scroll={{ x: `max-content` }}
                        dataSource={dataList}
                        columns={setTableHeader()}
                        rowKey={tableIdName}
                    />
                </Spin>
            </Card>
            <Modal
                title="加儲值額紀錄"
                visible={modalVisible}
                footer={null}
                style={{ maxWidth: 800 }}
                width="90%"
                onCancel={() => { setModalVisible(false) }}
                destroyOnClose
            >
                <Form layout="vertical" onFinish={createNewRecord}>
                    <Form.Item name="driver_id" label="司機">
                        <Input />
                    </Form.Item>
                    <Form.Item name="store_value" label="儲值額">
                        <InputNumber />
                    </Form.Item>

                    <Form.Item>
                        <Button
                            type="primary"
                            htmlType="submit"
                        >
                            {t('common.submit')}
                        </Button>
                    </Form.Item>
                </Form>
            </Modal>
        </NavAndSideFrame>
    );
}

export default DriverStoreValueList;
